import { useMemo } from 'react';
import { ESpotContainerType, useESpotDataFromName } from '@/data/Content/_ESpotDataFromName';
import { MARKETING_SPOT_DATA_TYPE } from '@/data/constants/marketing';
import { useProducts } from '@/data/Content/Products';
import { HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL } from '@/data/constants/product';
import {
	FamilyPoolSpots,
	limitFamilyPoolSize,
	parseFamilyPriority,
	pickVarietyPool,
} from '@/data/Content/RepurchaseFamilyPoolRules';
import { useContractByUserContext } from '@/data/Content/ContractByUserContext';

export const FAMILY_POOL_SIZE = 5;

const espotPartNumbers = (spots?: ESpotContainerType): string[] =>
	(spots?.MarketingSpotData?.at(0)?.baseMarketingSpotActivityData || [])
		.filter(
			({ baseMarketingSpotDataType }) =>
				baseMarketingSpotDataType === MARKETING_SPOT_DATA_TYPE.CATALOG_ENTRY ||
				baseMarketingSpotDataType === MARKETING_SPOT_DATA_TYPE.CATALOG_ENTRY_ID
		)
		.map(({ productPartNumber }) => productPartNumber || '')
		.filter(Boolean);

const useGenderPool = (emsName: string | undefined, size: number, priority: string[]) => {
	const { data, loading } = useESpotDataFromName(emsName, false);
	const partNumbers = useMemo(() => espotPartNumbers(data), [data]);
	const contract = useContractByUserContext();
	const { products, loading: loadingProducts } = useProducts({
		partNumber: partNumbers,
		contractId: contract,
		query: { profileName: HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL },
	});
	const pool = useMemo(
		() =>
			pickVarietyPool(
				[...products].sort(
					(a, b) => partNumbers.indexOf(a.partNumber) - partNumbers.indexOf(b.partNumber)
				),
				{ size, priority }
			),
		[products, partNumbers, size, priority]
	);

	return { pool, loading: !!emsName && (loading || loadingProducts) };
};

export const usePersonalizationFamilyPool = ({
	spots,
	size = FAMILY_POOL_SIZE,
	priority: rawPriority,
}: {
	spots: FamilyPoolSpots;
	size?: number;
	priority?: string;
}) => {
	const priority = useMemo(() => parseFamilyPriority(rawPriority), [rawPriority]);
	const poolSize = limitFamilyPoolSize(
		size,
		[spots.hombre, spots.mujer, spots.nino].filter(Boolean).length
	);
	const hombre = useGenderPool(spots.hombre, poolSize, priority);
	const mujer = useGenderPool(spots.mujer, poolSize, priority);
	const nino = useGenderPool(spots.nino, poolSize, priority);
	const partNumbers = useMemo(
		() => [...hombre.pool, ...mujer.pool, ...nino.pool],
		[hombre.pool, mujer.pool, nino.pool]
	);

	return {
		partNumbers,
		loading: hombre.loading || mujer.loading || nino.loading,
	};
};
